/**
 * Inbox hooks — Phase 08.
 *
 * Reads the backend `/inbox` and `/inbox/grouped` endpoints with a 5s
 * polling cadence (Plan 08's locked-in live-updates choice for v1).
 * Mutations call through `vnextApiClient` and trigger a `refetch()` so
 * the UI feels immediate without optimistic plumbing.
 *
 * Three reads:
 * - `useInbox(filters)` — flat list, drives Upcoming and per-entity
 *   detail views.
 * - `useInboxGrouped(filters)` — codename-grouped list, drives the
 *   Actionable tab.
 * - `useInboxKinds()` — read-once, populates the Kind filter dropdown
 *   from the live registry.
 *
 * The mutations hook (`useInboxMutations`) intentionally lives in this
 * module because every mutator needs to invalidate the inbox queries
 * the consumer is reading; bundling them lets the page wire the two
 * concerns once.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  InboxAssignRequest,
  InboxDismissRequest,
  InboxGroupedParams,
  InboxGroupedResponse,
  InboxItem,
  InboxListParams,
  InboxListResponse,
  InboxResolveRequest,
  InboxSnoozeRequest,
} from '@/types/inbox';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

// 5-second polling cadence — Plan 08's locked-in choice for live
// updates v1. If polling-induced load proves too much we can drop to
// 10s or swap in SSE later (the hook shape doesn't change).
export const INBOX_POLL_MS = 5000;

interface UseInboxResult {
  items: InboxItem[];
  total: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const EMPTY_LIST: InboxListResponse = { items: [], total: 0 };

export function useInbox(
  params: InboxListParams,
  options?: { poll?: boolean; enabled?: boolean }
): UseInboxResult {
  // Stabilise the params reference so a caller passing a fresh object
  // literal every render doesn't restart the poll. Stringify is the
  // simplest stable hash — the params object is small and infrequently
  // changing.
  const key = JSON.stringify(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const poll = usePoll<InboxListResponse>({
    // `enabled: false` silences the hook entirely — no initial fetch,
    // no poll — so an inactive tab's params churning (e.g. the shared
    // `?q=` search) can't fan out to this endpoint. Flipping back to
    // enabled fetches fresh.
    enabled: options?.enabled !== false,
    interval: options?.poll === false ? null : INBOX_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listInbox(paramsRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  const response = poll.data ?? EMPTY_LIST;
  return {
    items: response.items,
    total: response.total,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch: poll.refetch,
  };
}

interface UseInboxGroupedResult {
  groups: InboxGroupedResponse['groups'];
  /** Codename families across ALL pages — drives the pager. */
  groupTotal: number;
  /** Items across all pages. */
  itemTotal: number;
  isLoading: boolean;
  error: Error | null;
  /** Always a FULL fetch (bypasses the ETag). */
  refetch: () => Promise<void>;
}

const EMPTY_GROUPED: InboxGroupedResponse = {
  groups: [],
  groupTotal: 0,
  itemTotal: 0,
  page: 1,
  pageSize: 20,
};

export function useInboxGrouped(
  params: InboxGroupedParams,
  options?: { poll?: boolean; enabled?: boolean }
): UseInboxGroupedResult {
  const key = JSON.stringify(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // The page-level error banner shows failures of reads the user is
  // waiting on (mount, filter change, refetch). A transient failure of a
  // background tick recovers on the next one and must not flash it.
  const [bannerError, setBannerError] = useState<Error | null>(null);

  const poll = usePoll<InboxGroupedResponse>({
    // Same contract as `useInbox`: `enabled: false` = no fetch at all,
    // so cross-tab param churn can't trigger the (expensive) grouped
    // read while the Queue isn't visible.
    enabled: options?.enabled !== false,
    interval: options?.poll === false ? null : INBOX_POLL_MS,
    // A new filtered set is a different token space; usePoll drops the
    // stored ETag on every dependency change.
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      // Scheduled ticks send the last ETag as If-None-Match and treat the
      // steady-state 304 as a no-op; a user-driven refetch is always a
      // full read (ctx.etag is null for manual fetches).
      const result = await vnextApiClient.listInboxGrouped(paramsRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      if (result === 'not-modified') {
        return UNCHANGED;
      }
      return withEtag(result.data, result.etag);
    },
    onData: () => setBannerError(null),
    onError: (err, { reason }) => {
      const e = toError(err);
      Sentry.captureException(e);
      if (reason !== 'tick' && reason !== 'visible') {
        setBannerError(e);
      }
    },
  });

  const response = poll.data ?? EMPTY_GROUPED;
  return {
    groups: response.groups,
    groupTotal: response.groupTotal,
    itemTotal: response.itemTotal,
    isLoading: poll.isLoading,
    error: bannerError,
    refetch: poll.refetch,
  };
}

interface UseInboxKindsResult {
  kinds: string[];
  isLoading: boolean;
  error: Error | null;
}

/** Read-once: the registry is process-startup-stable on the backend. */
export function useInboxKinds(): UseInboxKindsResult {
  const [kinds, setKinds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    vnextApiClient
      .listInboxKinds()
      .then((kindList) => {
        if (!cancelled) {
          setKinds(kindList);
        }
      })
      .catch((err) => {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        if (!cancelled) {
          setError(e);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { kinds, isLoading, error };
}

interface UseInboxMutationsResult {
  snooze: (itemId: string, body: InboxSnoozeRequest) => Promise<InboxItem>;
  dismiss: (itemId: string, body: InboxDismissRequest) => Promise<InboxItem>;
  assign: (itemId: string, body: InboxAssignRequest) => Promise<InboxItem>;
  resolve: (itemId: string, body: InboxResolveRequest) => Promise<InboxItem>;
  markSeen: (ids: string[]) => Promise<number>;
}

/**
 * The mutations don't return promises that resolve to anything the
 * caller persists locally — they just hit the backend and let the
 * caller call `refetch()` on whichever list hook is in scope. Keeping
 * the two concerns separate (this hook vs. `useInbox` / `useInboxGrouped`)
 * matches the Phase 06 pattern (`useNpMissingBackofficeBanner.refetch`).
 */
export function useInboxMutations(): UseInboxMutationsResult {
  const snooze = useCallback(
    (itemId: string, body: InboxSnoozeRequest) => vnextApiClient.snoozeInbox(itemId, body),
    []
  );
  const dismiss = useCallback(
    (itemId: string, body: InboxDismissRequest) => vnextApiClient.dismissInbox(itemId, body),
    []
  );
  const assign = useCallback(
    (itemId: string, body: InboxAssignRequest) => vnextApiClient.assignInbox(itemId, body),
    []
  );
  const resolve = useCallback(
    (itemId: string, body: InboxResolveRequest) => vnextApiClient.resolveInbox(itemId, body),
    []
  );
  const markSeen = useCallback((ids: string[]) => vnextApiClient.markInboxSeen(ids), []);

  return { snooze, dismiss, assign, resolve, markSeen };
}
